import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import {Params, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {combineLatest, Observable, Subject} from 'rxjs';
import * as fromBrandPLP from '../../store';
import { getSortOption} from '~app/modules/brand-plp/store';
import {Product} from '~shared/models';
import {DEFAULT_LIMIT} from '~app/modules/generic/constants';
import {botDetector} from '~app/modules/generic/helpers';
import {NavigationHelper, NavigationHelperConfig} from '~shared/services/navigation-helper.service';
import {DOCUMENT} from '@angular/common';
import {map, takeUntil, tap} from 'rxjs/operators';
import {FilterBarService} from '~shared/services/filter-bar.service';
import {ApplicationEventFilterAreaInteractionType} from '~shared/modules/application-event/constants';
import {
  ElementVisibilityInViewportApplicationEvent,
  FilterAreaInteractionApplicationEvent,
  PLPPaginationShowMoreClickEvent,
  SortOption,
  SortOptionsEnum,
} from '~shared/modules/application-event/models';
import {ApplicationEventService} from '~shared/modules/application-event/services';
import {ProductsLoadingService} from '~app/modules/brand-plp/services/products-loading.service';
import {isTagsVisible} from '~app/modules/filters/store';
import {Category} from '~app/modules/generic/models';
import {NEWSLETTER_PAGE_SOURCE, NUMBER_40} from '~shared/constants';
import {GATrackingEventType} from '~shared/modules/ga-tracking/constants';
import {Components} from '~app/modules/storyblok/constants';
import {Pagination} from '~app/modules/catalog/models/pagination.model';
import {getCountryDomainCode} from '~server/utils/country-detection.util';
import {ActiveFeaturesConfigcatService} from '~shared/modules/active-features/services';
import {REQUEST} from '@nguniversal/express-engine/tokens';
import {IdentityService} from '~core/modules/identity/services/identity.service';
import {PriceTypes} from '~shared/modules/price-type-toggler/models/price-types.enum';
import {PriceTypeService} from '~shared/modules/price-type-toggler/services';
import {VATDisclaimerService} from '~shared/services/vat-disclaimer.service';
import {PlpVariantRestyleService} from '~shared/services/plp-variant-restyle/plp-variant-restyle.service';
import {DEFAULT_LIMIT_OF_PRODUCTS} from '~app/modules/catalog/constants/catalog-products-list.constants';
import {TooltipPositions} from '~shared/modules/tooltip/models/tooltip-positions.model';
import {SpecificFilterBarService, SpecificFilterTypeConfig} from '~shared/services/specific-filter-bar.service';
import {FilterFacetValue} from '~app/modules/catalog/models';
import {getQueryParams} from '~core/store';
import { InputsType } from 'ng-dynamic-component';

@Component({
  selector: 'app-brand-plp-body',
  templateUrl: './brand-plp-body.component.html',
  styleUrls: ['./brand-plp-body.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandPLPBodyComponent implements OnInit, AfterViewChecked, OnDestroy {
  @Input() totalCount: number;
  @Input() queryParams: Params;
  @Input() isMobile: boolean;
  @Input() isTablet: boolean;
  @Input() isDesktop: boolean;
  @Input() isMobileOrTablet: boolean;
  @Input() products: Product[] = [];
  @Input() categories: Category[] = [];
  @Input() brandName: string;
  @Input() isZeroResult: boolean;

  isBot = false;
  isStickyActions = false;
  layoutChanged = false;
  isFiltersBarOpened$: Observable<boolean>;
  isZeroResult$: Observable<boolean>;
  isTagsVisible$: Observable<boolean>;
  readonly brandPlpSource = NEWSLETTER_PAGE_SOURCE.BRAND_PLP;

  // Storyblok
  @Input() storyBottomContent: Array<Record<string, unknown>> = [];
  @Input() storyEditable: string;
  components = Components;

  private unsubscribe$ = new Subject();
  categoryName: string;

  applicationEventFilterAreaInteractionType = ApplicationEventFilterAreaInteractionType;
  isVATDisclaimerEnabled: boolean;

  isLoggedIn$ = this.identityService.isLoggedIn();
  isB2CUser$: Observable<boolean> = this.priceTypeService.priceType$.pipe(
    map((priceType: PriceTypes) => priceType === PriceTypes.b2c)
  );
  isPLP: boolean;
  isNewPLPVariantRestyle: boolean;
  zeroResultsPage: boolean;
  showFilterFacets: boolean;
  hasFilters: boolean;
  isExpired: boolean;
  specificFilterType: SpecificFilterTypeConfig;

  defaultLimit = DEFAULT_LIMIT;
  defaultProductsLimit = DEFAULT_LIMIT_OF_PRODUCTS;
  selectedOption$: Observable<SortOption>;
  bestMatchSortOption = SortOptionsEnum.BEST_MATCH;
  tooltipPosition = TooltipPositions.center;
  bulletBackgroundColor = '#ffff'; // grey-tint-80
  bulletTextColor = '#99ABC7'; // white

  constructor(
    private router: Router,
    private store$: Store,
    private navigationHelper: NavigationHelper,
    private filterBarService: FilterBarService,
    private cd: ChangeDetectorRef,
    private productsLoadingService: ProductsLoadingService,
    @Inject(DOCUMENT) private document: Document,
    private applicationEventService: ApplicationEventService,
    private activeFeaturesConfigCatService: ActiveFeaturesConfigcatService,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any,
    private identityService: IdentityService,
    private priceTypeService: PriceTypeService,
    private readonly vatDisclaimerService: VATDisclaimerService,
    private plpVariantRestyleService: PlpVariantRestyleService,
    private specificFilterBarService: SpecificFilterBarService
  ) {
    this.isVATDisclaimerEnabled = this.vatDisclaimerService.getVATDisclaimerForCountry(
      getCountryDomainCode(this.webAppBuyerRequest)
    );
    this.selectedOption$ = this.store$.pipe(select(getSortOption));
  }

  ngAfterViewChecked(): void {
    this.isBot = botDetector(navigator.userAgent);
  }

  get isFirstPage(): boolean {
    return !this.queryParams.offset || +this.queryParams.offset < NUMBER_40;
  }

  ngOnInit(): void {
    this.initializeFilters();
    this.initialTagVisible();
    this.initialZeroResult();
    this.initialIsFilterBarOpen();
    this.initialIsSpecificFilterBarOpen();
    this.plpVariantRestyleService
      .isNewPLPVariantRestyle()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        this.isNewPLPVariantRestyle = value;
      });
    this.isPLP = this.plpVariantRestyleService.isPLP();
  }

  ngOnChanges() {
    this.zeroResultsPage = this.isZeroResult && !this.isExpired && !this.hasFilters;
    this.showFilterFacets = !this.zeroResultsPage;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onPageSelected(pagination: Pagination): void {
    const { limit, offset, ...rest } = this.queryParams || {};

    this.applicationEventService.publish(new PLPPaginationShowMoreClickEvent(pagination, DEFAULT_LIMIT));

    this.updateRouterState({
      limit: pagination.limit,
      offset: pagination.offset,
      ...rest,
    });
  }

  onActionsStickyStatusChange(isSticky: boolean): void {
    this.isStickyActions = isSticky;
  }

  onPaginationVisibilityChange(isVisible: boolean): void {
    this.fireElementVisibility(`Pagination ${isVisible ? 'is' : 'is not'}`);
  }

  onProductVisibility(productPosition: number): void {
    this.fireElementVisibility(`Product ${productPosition} is`);
  }

  private getCurrentPath(): string {
    return this.router.url.split('?')[0].slice(1);
  }

  private updateRouterState(params?: Params, config?: NavigationHelperConfig): void {
    const path = this.getCurrentPath();
    this.navigationHelper.replaceUrl([path], { queryParams: params }, config);
  }

  buildInputs(blokInputs: InputsType): InputsType {
    return {
      ...blokInputs,
      ...{
        isMobile: this.isMobile,
        isTablet: this.isTablet,
        isDesktop: this.isDesktop,
      },
    };
  }

  private initialTagVisible(): void {
    this.isTagsVisible$ = this.store$.pipe(
      select(isTagsVisible),
      tap(() => this.cd.markForCheck())
    );
  }

  private initialIsFilterBarOpen(): void {
    this.isFiltersBarOpened$ = this.filterBarService.isFilterBarOpened.pipe(
      tap((isOpened: boolean) => {
        this.toggleScrollbar(isOpened);
        this.cd.markForCheck();
      })
    );
  }

  private initialZeroResult(): void {
    this.isZeroResult$ = combineLatest([
      this.store$.pipe(select(fromBrandPLP.getProducts)),
      this.productsLoadingService.isProductsLoading$,
    ]).pipe(
      map(([products, isLoadingProducts]) => {
        return !isLoadingProducts && products.length < 1;
      }),
      tap(() => this.cd.markForCheck())
    );
  }

  private toggleScrollbar(isOpened: boolean): void {
    this.document.body.classList.toggle('noscroll', isOpened);
  }

  private fireFilterInteractionEvent(type: ApplicationEventFilterAreaInteractionType): void {
    this.applicationEventService.publish(new FilterAreaInteractionApplicationEvent(type));
  }

  toggleFilters(isOpened: boolean, type: ApplicationEventFilterAreaInteractionType): void {
    this.filterBarService.toggleFilterBar(isOpened);
    this.toggleScrollbar(isOpened);
    this.fireFilterInteractionEvent(type);
  }

  private fireElementVisibility(label: string): void {
    this.applicationEventService.publish(
      new ElementVisibilityInViewportApplicationEvent(GATrackingEventType.PLPScrolling, label)
    );
  }

  toggleSpecificFilters(type: ApplicationEventFilterAreaInteractionType): void {
    this.specificFilterBarService.toggleFilterBar(null);
    this.fireFilterInteractionEvent(type);
  }

  togglePriceFilter() {
    this.specificFilterBarService.toggleFilterBar({
      type: 'price',
    });
  }

  toggleCategoryFilter() {
    this.specificFilterBarService.toggleFilterBar({
      type: 'category',
    });
  }

  toggleAttributeFilter(filterFacetValue: FilterFacetValue) {
    this.specificFilterBarService.toggleFilterBar({
      type: 'attribute',
      options: {
        attribute: filterFacetValue,
      },
    });
  }

  private initializeFilters(): void {
    this.store$.pipe(select(getQueryParams), takeUntil(this.unsubscribe$)).subscribe((queryParams: Params) => {
      const filters = queryParams.filter || {};
      this.hasFilters = !!Object.keys(filters)?.length;
      const hasPromoTag: boolean = !!(filters.promoTag || false);
      this.isExpired = this.hasFilters && hasPromoTag;
    });
  }

  private initialIsSpecificFilterBarOpen(): void {
    this.specificFilterBarService.isSpecificFilterBarOpened.subscribe((filterType: SpecificFilterTypeConfig) => {
      this.toggleScrollbar(!!filterType);
      this.specificFilterType = filterType;
      this.cd.markForCheck();
    });
  }

  protected readonly DEFAULT_LIMIT_OF_PRODUCTS = DEFAULT_LIMIT_OF_PRODUCTS;
}
